import { useCallback, useEffect, useRef, useState } from 'react';
import { GOOGLE_CLIENT_ID } from '../config/runtimeConfig';
import { BACKEND_LANGUAGE_IDS, getPreferredLanguage } from '../constants/Languages';
import loadGoogleIdentityScript from '../utils/loadGoogleIdentityScript';
import {
  buildGoogleOAuthStartUrl,
  shouldUseOAuthRedirect,
} from '../utils/googleAuthUtils';
import useCookieConsent from './useCookieConsent';

export default function useGoogleSignInButton(onCredential, { buttonText = 'signin_with' } = {}) {
  const buttonRef = useRef(null);
  const [scriptError, setScriptError] = useState(false);
  const { consent, openPreferences } = useCookieConsent();
  const thirdPartyAllowed = consent?.thirdParty === true;
  const useOAuthRedirect = shouldUseOAuthRedirect();

  const redirectToGoogleOAuth = useCallback(() => {
    const language = BACKEND_LANGUAGE_IDS[getPreferredLanguage()];
    window.location.assign(buildGoogleOAuthStartUrl(window.location.href, language));
  }, []);

  const initializeGoogleSignIn = useCallback(async () => {
    if (useOAuthRedirect || !thirdPartyAllowed || !GOOGLE_CLIENT_ID || !buttonRef.current) {
      return;
    }

    try {
      await loadGoogleIdentityScript();

      if (!window.google?.accounts || !buttonRef.current) {
        return;
      }

      if (buttonRef.current.hasChildNodes()) {
        buttonRef.current.innerHTML = '';
      }

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: onCredential,
      });

      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: 'outline',
        size: 'large',
        text: buttonText,
        locale: getPreferredLanguage(),
      });
      setScriptError(false);
    } catch (error) {
      console.error('Error initializing Google Sign-In:', error);
      setScriptError(true);
    }
  }, [buttonText, onCredential, thirdPartyAllowed, useOAuthRedirect]);

  useEffect(() => {
    if (useOAuthRedirect || !thirdPartyAllowed) {
      if (buttonRef.current) {
        buttonRef.current.innerHTML = '';
      }
      return undefined;
    }

    const timeoutId = setTimeout(initializeGoogleSignIn, 150);
    return () => clearTimeout(timeoutId);
  }, [initializeGoogleSignIn, thirdPartyAllowed, useOAuthRedirect]);

  return {
    buttonRef,
    thirdPartyAllowed,
    openPreferences,
    scriptError,
    useOAuthRedirect,
    redirectToGoogleOAuth,
  };
}
